import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { collectionGroup, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/FirebaseCredential";
import { RootState } from "../store/store";


interface PaymentState {
  methods: any[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null; // timestamp of last fetch
}

const initialState: PaymentState = {
  methods: [],
  loading: false,
  error: null,
  lastFetched: null,
};

// ✅ Async thunk with caching (manual refresh supported)
export const fetchPaymentMethods = createAsyncThunk<
  any[], // return type
  { companyId: string; force?: boolean }, // args type
  { state: RootState }
>(
  "payments/fetchPaymentMethods",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error("Company ID is required");

      const paymentQuery = query(
        collectionGroup(db, "paymentcollection"),
        where("company_id", "==", companyId)
      );

      const querySnapshot = await getDocs(paymentQuery);
      const paymentData: any[] = [];

      querySnapshot.forEach((doc) => {
        paymentData.push({ id: doc.id, ...doc.data() });
      });

      return paymentData;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  },
  {
    condition: ({ force = false }, { getState }) => {
      const { payments } = getState();
      if (!force && payments.methods.length > 0) {
        // ✅ Skip Firestore call if we already have data
        return false;
      }
      return true;
    },
  }
);

const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearPayments: (state) => {
      state.methods = [];
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.methods = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPayments } = paymentSlice.actions;
export default paymentSlice.reducer;
